"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CO_BRAND_LANDING_PAGES = exports.FOREST_RIVER_PARTNER_SLUG = void 0;
exports.canonicalizeCoBrandSlug = canonicalizeCoBrandSlug;
exports.getCoBrandLandingPage = getCoBrandLandingPage;
exports.isCoBrandLandingPage = isCoBrandLandingPage;
exports.getCoBrandPartnerSlugs = getCoBrandPartnerSlugs;
/*
 * Cobrand landing pages are data, not enum members. Each partner owns one static path segment (its
 * own LandingPagePath, e.g. FOREST_RIVER = 'forestriver') and this catalog carries the per-product
 * detail that would otherwise need an enum member, a product-config entry and a policy entry apiece.
 *
 * URL shape is /<partnerSlug>/<dealerSlug>/<productSlug>: a row without a productSlug is the
 * partner's umbrella page. Dealers are deliberately absent from this catalog -- they are unbounded
 * and free-form, carried in the URL for attribution and display only, and never affect which
 * product or policy a page resolves.
 */
const avenProduct_1 = require("../avenProduct");
const cobrandPartner_1 = require("../cobrandPartner");
const flows_1 = require("../flows");
const uwSpecCompositorTemplate_1 = require("../uwSpecCompositorTemplate");
/** @TranslateToPython */
exports.FOREST_RIVER_PARTNER_SLUG = 'forestriver';
/**
 * One row per cobrand landing page. Adding a partner product is a row here, never a LandingPagePath member:
 * the route, the product config and the policy lookup all resolve off partnerSlug/productSlug at runtime.
 *
 * TODO(#98870): the partnerDetails product catalog becomes the source of truth for a partner's products and
 * their partner-specific display names; this stays as the fallback for partners with no catalog row yet.
 * https://github.com/heraclescorp/heracles/pull/98870
 *
 * @TranslateToPython
 */
exports.CO_BRAND_LANDING_PAGES = [
    {
        partnerId: cobrandPartner_1.CobrandPartnerId.ForestRiver,
        partnerSlug: exports.FOREST_RIVER_PARTNER_SLUG,
        productSlug: undefined,
        product: avenProduct_1.AvenProductOrUnknown.HOME,
        entryFlows: [flows_1.Flows.origination, flows_1.Flows.uccOrigination],
        templateSlot: uwSpecCompositorTemplate_1.UwSpecCompositorTemplateSlot.HomeDefaultAvenCash,
    },
    {
        partnerId: cobrandPartner_1.CobrandPartnerId.ForestRiver,
        partnerSlug: exports.FOREST_RIVER_PARTNER_SLUG,
        productSlug: 'homeline',
        product: avenProduct_1.AvenProductOrUnknown.HOME,
        entryFlows: [flows_1.Flows.origination, flows_1.Flows.uccOrigination],
        templateSlot: uwSpecCompositorTemplate_1.UwSpecCompositorTemplateSlot.HomeDefaultAvenCash,
    },
    {
        partnerId: cobrandPartner_1.CobrandPartnerId.ForestRiver,
        partnerSlug: exports.FOREST_RIVER_PARTNER_SLUG,
        productSlug: 'journey',
        product: avenProduct_1.AvenProductOrUnknown.UCC,
        entryFlows: [flows_1.Flows.uccOrigination],
        templateSlot: uwSpecCompositorTemplate_1.UwSpecCompositorTemplateSlot.UccDefaultRewards,
    },
    {
        partnerId: cobrandPartner_1.CobrandPartnerId.ForestRiver,
        partnerSlug: exports.FOREST_RIVER_PARTNER_SLUG,
        productSlug: 'trailhead',
        product: avenProduct_1.AvenProductOrUnknown.DEPOSIT,
        entryFlows: [flows_1.Flows.depositOrigination],
        templateSlot: undefined,
    },
];
// Same normalize/strip/lowercase/dash pipeline as canonicalizeCitySlug, and must stay in lockstep with any
// Python twin: a divergence silently drops a cobrand page to the CO_BRAND default policy.
function canonicalizeCoBrandSlug(slug) {
    return slug
        .normalize('NFKD')
        .replace(/[^\x00-\x7F]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
const CO_BRAND_PAGE_BY_PARTNER_AND_PRODUCT_SLUG = new Map(exports.CO_BRAND_LANDING_PAGES.map((entry) => [`${entry.partnerSlug}/${entry.productSlug ?? ''}`, entry]));
function getCoBrandLandingPage(partnerSlug, productSlug) {
    const canonicalPartnerSlug = partnerSlug ? canonicalizeCoBrandSlug(partnerSlug) : '';
    if (!canonicalPartnerSlug) {
        return undefined;
    }
    // A product segment that canonicalizes to nothing (e.g. '/partners/forestriver/somedealer/---') is a bad
    // product, not the umbrella page. Collapsing it to '' would match the umbrella row here while the Python
    // twin returns None, so the two stacks would disagree on whether the URL is even a cobrand page.
    if (productSlug !== undefined) {
        const canonicalProductSlug = canonicalizeCoBrandSlug(productSlug);
        if (!canonicalProductSlug) {
            return undefined;
        }
        return CO_BRAND_PAGE_BY_PARTNER_AND_PRODUCT_SLUG.get(`${canonicalPartnerSlug}/${canonicalProductSlug}`);
    }
    return CO_BRAND_PAGE_BY_PARTNER_AND_PRODUCT_SLUG.get(`${canonicalPartnerSlug}/`);
}
function isCoBrandLandingPage(partnerSlug, productSlug) {
    return getCoBrandLandingPage(partnerSlug, productSlug) !== undefined;
}
const CO_BRAND_PARTNER_SLUGS = Array.from(new Set(exports.CO_BRAND_LANDING_PAGES.map((entry) => entry.partnerSlug)));
function getCoBrandPartnerSlugs() {
    return CO_BRAND_PARTNER_SLUGS;
}
